import { auth } from "../firebase/config";
import { AppGrowthResponse } from "../types/appGrowth";

const FETCH_APP_GROWTH_ENDPOINT =
  process.env.REACT_APP_FETCH_APP_GROWTH_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/fetchAppGrowth";

class AppGrowthService {
  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      throw new Error(
        detailedMessage || `Request failed with status ${response.status}`,
      );
    }

    return responseData as T;
  }

  async fetchGrowth(
    periodDays = 7,
    appIds: string[] = [],
  ): Promise<AppGrowthResponse> {
    const normalizedPeriod = [7, 14, 30, 60, 90, 180].includes(periodDays)
      ? periodDays
      : 7;
    const normalizedAppIds = Array.from(
      new Set(
        (Array.isArray(appIds) ? appIds : [])
          .map((item) => String(item || "").trim())
          .filter(Boolean),
      ),
    );

    const response = await this.postAuthenticatedJson<AppGrowthResponse>(
      FETCH_APP_GROWTH_ENDPOINT,
      { periodDays: normalizedPeriod, appIds: normalizedAppIds },
    );

    const hasRevenueSchema =
      typeof response?.aggregate?.totalRevenue === "number" &&
      (response.apps || []).every(
        (app) => typeof app.totalRevenue === "number",
      );
    if (response?.periodDays !== normalizedPeriod || !hasRevenueSchema) {
      throw new Error(
        "The App Growth backend is out of date. Deploy the latest fetchAppGrowth Cloud Function and try again.",
      );
    }

    return response;
  }
}

const appGrowthService = new AppGrowthService();
export default appGrowthService;
